import type { MockSectionCustomerInput } from './mockSectionData.util';
import { mockSectionData } from './mockSectionData.util';
import { coerceBhConnectionCount } from './bhConnectionCoerce.util';
import {
  coerceBhMedicalDiagnosisRows,
  type BhMedicalDiagnosisRow,
} from './bhMedicalDiagnosesSnapshot.util';
import type { ReportSectionFieldSources } from './reportFieldSource.util';
import { readReportFieldSource } from './reportFieldSource.util';
import { isWarehouseWhoWereReachingSnapshot } from './whoWereReachingSnapshotDetect.util';

const WAREHOUSE_FIELD_SOURCES: ReportSectionFieldSources = {
  totalNewBhDx: 'warehouse',
  totalNewMedicalDx: 'warehouse',
  breakdown: 'warehouse',
  medical: 'warehouse',
  whyDenominator: 'mock',
  insights: 'mock',
};

const MOCK_FIELD_SOURCES: ReportSectionFieldSources = {
  totalNewBhDx: 'mock',
  totalNewMedicalDx: 'mock',
  breakdown: 'mock',
  medical: 'mock',
  whyDenominator: 'mock',
  insights: 'mock',
};

const readBreakdownRows = (
  rows: unknown,
): Array<Record<string, unknown>> =>
  Array.isArray(rows) ? (rows as Array<Record<string, unknown>>) : [];

const resolveBreakdown = (
  snapshotRows: unknown,
  mockRows: unknown,
  useMockFallback: boolean,
): BhMedicalDiagnosisRow[] => {
  const fromSnapshot = coerceBhMedicalDiagnosisRows(snapshotRows);
  if (fromSnapshot.length > 0) {
    return fromSnapshot;
  }
  if (!useMockFallback) {
    return fromSnapshot;
  }
  return coerceBhMedicalDiagnosisRows(mockRows);
};

const snapshotHasWarehouseDxRows = (snapshot: Record<string, unknown>): boolean => {
  const rows = [
    ...readBreakdownRows(snapshot.breakdown),
    ...readBreakdownRows(snapshot.medical),
  ];
  return rows.some(row => {
    if (!row || typeof row !== 'object') {
      return false;
    }
    return (
      typeof row.condition === 'string' ||
      typeof row.diagnosis === 'string' ||
      typeof row.category === 'string' ||
      (typeof row.type === 'string' && row.type.trim().length > 0)
    );
  });
};

const resolveTotal = (
  snapshotValue: unknown,
  mockValue: unknown,
  useMockFallback: boolean,
): number => {
  const fromSnapshot = coerceBhConnectionCount(snapshotValue);
  if (fromSnapshot > 0) {
    return fromSnapshot;
  }
  return useMockFallback ? coerceBhConnectionCount(mockValue) : 0;
};

/**
 * Normalizes bh-medical-diagnoses snapshots with `fieldSources` for mock framing.
 * @param snapshot - Stored or live snapshot JSON.
 * @param customer - Customer context for mock fallback fields.
 * @param reportingPeriod - Report period label.
 * @returns Snapshot with `fieldSources` metadata for the Care Portal UI.
 */
export const normalizeBhMedicalDiagnosesSnapshot = (
  snapshot: Record<string, unknown>,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> => {
  const mockBase = mockSectionData(
    'bh-medical-diagnoses',
    customer,
    reportingPeriod,
  );
  const isWarehouse =
    isWarehouseWhoWereReachingSnapshot(snapshot) ||
    readReportFieldSource(snapshot, 'breakdown') === 'warehouse' ||
    readReportFieldSource(snapshot, 'totalNewBhDx') === 'warehouse' ||
    snapshot.yearMo != null ||
    snapshotHasWarehouseDxRows(snapshot);
  const useMockFallback = !isWarehouse;

  const breakdown = resolveBreakdown(
    snapshot.breakdown,
    mockBase.breakdown,
    useMockFallback,
  );
  const medical = resolveBreakdown(snapshot.medical, mockBase.medical, useMockFallback);
  const totalNewBhDx =
    resolveTotal(snapshot.totalNewBhDx, mockBase.totalNewBhDx, useMockFallback) ||
    breakdown.reduce((sum, row) => sum + row.count, 0);
  const totalNewMedicalDx =
    resolveTotal(
      snapshot.totalNewMedicalDx,
      mockBase.totalNewMedicalDx,
      useMockFallback,
    ) || medical.reduce((sum, row) => sum + row.count, 0);

  return {
    ...mockBase,
    ...snapshot,
    breakdown,
    medical,
    totalNewBhDx,
    totalNewMedicalDx,
    fieldSources: isWarehouse
      ? {
          ...MOCK_FIELD_SOURCES,
          ...WAREHOUSE_FIELD_SOURCES,
          ...(typeof snapshot.fieldSources === 'object' && snapshot.fieldSources !== null
            ? (snapshot.fieldSources as ReportSectionFieldSources)
            : {}),
          whyDenominator: 'mock',
          insights: 'mock',
        }
      : MOCK_FIELD_SOURCES,
    snapshotSource: isWarehouse ? snapshot.snapshotSource ?? 'warehouse' : undefined,
  };
};
